import { useState, useCallback } from "react";
import { createContainer } from "unstated-next";

import {
    DemoQuestion,
    CubeQuery,
    CubeQueryResult,
    CubeMeta,
    fetchDemoQuestions,
    executeQuery,
    fetchCubeList,
    fetchCubeMeta,
    fetchCubeMeasures,
    fetchCubeDimensions
} from "../../../api/statistics/endpoints-referrers";

function useStatistics() {
    const [demoQuestions, setDemoQuestions] = useState<DemoQuestion[]>([]);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Load demo questions from the backend
     */
    const loadDemoQuestions = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await fetchDemoQuestions();
            setDemoQuestions(response.data || []);
            setIsReady(true);
        } catch (err: any) {
            setError(err?.message || "Failed to load demo questions");
            setIsReady(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Execute a Cube.js query. The backend universally scopes
     * `question_answer_log` queries to the caller's current knowledge base.
     */
    const query = useCallback(async (cubeQuery: CubeQuery): Promise<CubeQueryResult | null> => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await executeQuery(cubeQuery);
            return response.data;
        } catch (err: any) {
            setError(err?.message || "Query failed");
            return null;
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Get list of available cubes
     */
    const getCubes = useCallback(async (): Promise<string[]> => {
        try {
            const response = await fetchCubeList();
            return response.data || [];
        } catch (err: any) {
            setError(err?.message || "Failed to get cubes");
            return [];
        }
    }, []);

    /**
     * Get metadata for a specific cube
     */
    const getCubeMeta = useCallback(async (cubeName: string): Promise<CubeMeta | null> => {
        try {
            const response = await fetchCubeMeta(cubeName);
            return response.data;
        } catch (err: any) {
            setError(err?.message || "Failed to get cube metadata");
            return null;
        }
    }, []);

    /**
     * Get measures for a specific cube
     */
    const getMeasures = useCallback(async (cubeName: string) => {
        try {
            const response = await fetchCubeMeasures(cubeName);
            return response.data || [];
        } catch (err: any) {
            setError(err?.message || "Failed to get measures");
            return [];
        }
    }, []);

    /**
     * Get dimensions for a specific cube
     */
    const getDimensions = useCallback(async (cubeName: string) => {
        try {
            const response = await fetchCubeDimensions(cubeName);
            return response.data || [];
        } catch (err: any) {
            setError(err?.message || "Failed to get dimensions");
            return [];
        }
    }, []);

    /**
     * Initialize - called by auth containers when user is authenticated
     */
    const initialize = useCallback(async () => {
        await loadDemoQuestions();
    }, [loadDemoQuestions]);

    /**
     * Reset state - called when user logs out
     */
    const reset = useCallback(() => {
        setDemoQuestions([]);
        setIsReady(false);
        setIsLoading(false);
        setError(null);
    }, []);

    return {
        // State
        demoQuestions,
        isReady,
        isLoading,
        error,
        // Methods
        initialize,
        reset,
        query,
        getCubes,
        getCubeMeta,
        getMeasures,
        getDimensions
    };
}

export const StatisticsContainer = createContainer(useStatistics);
